import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  AdminUser,
  CreateAdminInput,
  SyncGoogleIdentitiesResult,
} from "./types";

const client = generateClient<Schema>();

interface RawAdminUser {
  sub: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  status: string;
  createdAt: string;
  adminProvisioned: boolean;
  hasGoogleLogin: boolean;
}

function mapAdminUser(raw: RawAdminUser): AdminUser {
  return {
    sub: raw.sub,
    username: raw.username,
    email: raw.email,
    firstName: raw.firstName ?? "",
    lastName: raw.lastName ?? "",
    status: raw.status === "active" ? "active" : "disabled",
    createdAt: raw.createdAt,
    adminProvisioned: raw.adminProvisioned,
    hasGoogleLogin: raw.hasGoogleLogin,
  };
}

export class AdminsRepository {
  async list(): Promise<AdminUser[]> {
    const { data, errors } = await client.mutations.AdminUsers({
      action: "list",
    });
    if (errors?.length) throw new Error(errors[0].message);
    const items = (data?.items ?? []) as RawAdminUser[];
    return items.map(mapAdminUser);
  }

  async create(input: CreateAdminInput): Promise<{
    success: boolean;
    mode: "created" | "promoted" | "linked" | "";
    errorCode: string | null;
    errors: string[];
  }> {
    const { data, errors } = await client.mutations.AdminUsers({
      action: "create",
      input: { user: input },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return {
      success: data?.success ?? false,
      mode: (data?.mode ?? "") as "created" | "promoted" | "linked" | "",
      errorCode: data?.errorCode ?? null,
      errors: (data?.errors ?? []).filter((e): e is string => e !== null),
    };
  }

  async syncGoogleIdentities(): Promise<SyncGoogleIdentitiesResult> {
    const { data, errors } = await client.mutations.AdminUsers({
      action: "syncGoogleIdentities",
    });
    if (errors?.length) throw new Error(errors[0].message);
    return {
      success: data?.success ?? false,
      linkedEmails: (data?.linkedEmails ?? []).filter(
        (e): e is string => e !== null,
      ),
      skippedCount: data?.skippedCount ?? 0,
      errors: (data?.errors ?? []).filter((e): e is string => e !== null),
    };
  }

  async deactivate(sub: string): Promise<{
    success: boolean;
    errors: string[];
  }> {
    const { data, errors } = await client.mutations.AdminUsers({
      action: "deactivate",
      input: { sub },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return {
      success: data?.success ?? false,
      errors: (data?.errors ?? []).filter((e): e is string => e !== null),
    };
  }
}
